import { useState, useCallback, useEffect } from "react";
import employeeTransactionAccessService from "../services/employeeTransactionAccessService";
import { SetAuditTrail, SetAuditTrailBulk } from "../../components/Reports/AuditTrailReportEmployeeSetup";
import { useGeneralMessage } from "../../common/APICommon";

export const useEmployeeTransactionAccess = () => {
  const [loading, setLoading] = useState(false);
  
  // States for Tab 1 (Module Access)
  const [moduleData, setModuleData] = useState([]);
  const [filterData, setFilterData] = useState([]);

  // States for Tab 2 (Processor Access)
  const [processorData, setProcessorData] = useState([]);
  const [processorFilterData, setProcessorFilterData] = useState([]);

  const { showGeneralMessage, showMessage, message, isSuccess } = useGeneralMessage();

  // ── Fetching Data (Tab 1) ──────────────────────────────────────────────────────────
  const fetchModuleAccessData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await employeeTransactionAccessService.getAll();

      setModuleData(result.data || []);
      setFilterData(result.data || []);
      
    } catch (err) {
      console.error("Failed to fetch access data", err);
      showGeneralMessage("Failed to load module access templates", false);
    } finally {
      setLoading(false);
    }
  }, [showGeneralMessage]);

  useEffect(() => {
    fetchModuleAccessData();
  }, [fetchModuleAccessData]);

  // ── Fetching Data (Tab 2) ──────────────────────────────────────────────────────────
  const fetchProcessorAccessData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await employeeTransactionAccessService.getAllProcessorAccess();

      setProcessorData(result.data || []);
      setProcessorFilterData(result.data || []);
    } catch (err) {
      console.error("Failed to fetch access data", err);
      showGeneralMessage("Failed to load processoraccess templates", false);
    } finally {
      setLoading(false);
    }
  }, [showGeneralMessage]);

  useEffect(() => {
    fetchProcessorAccessData();
  }, [fetchProcessorAccessData]);

  // ── Module Access Actions (Tab 1) ─────────────────────────────────────────
  const updateAccess = async (id, payload, auditData) => {
    setLoading(true);
    try {
      const res = await employeeTransactionAccessService.update(id, payload);
      if (auditData && auditData.length > 0) await SetAuditTrailBulk({ data: auditData });
      showGeneralMessage("Module Access updated successfully", true);
      await fetchModuleAccessData();
      return { success: true };
    } catch (err) {
      showGeneralMessage(err.response?.data?.message || "Update failed", false);
      return { success: false };
    } finally { setLoading(false); }
  };

  // ── Processor Access Actions (Tab 2) ──────────────────────────────────────
  const saveProcessorAccess = async (form, id = null, auditData) => {
    setLoading(true);
    try {
      if (id) {
        await employeeTransactionAccessService.updateProcessorAccess(id, form);
        if (auditData && auditData.length > 0) await SetAuditTrailBulk({ data: auditData });
      } else {
        await employeeTransactionAccessService.createProcessorAccess(form);
        if (auditData) await SetAuditTrail(auditData);
      }
      showGeneralMessage("Processor Access saved successfully", true);
      await fetchProcessorAccessData();
      return { success: true };
    } catch (err) {
      showGeneralMessage(err.response?.data?.message || "Save failed", false);
      return { success: false };
    } finally { setLoading(false); }
  };

  const searchData = (value, activeTab) => {
    if (!value) {
      if (activeTab === 0) setModuleData(filterData);
      else setProcessorData(processorFilterData);
      return;
    }
    const lower = value.toLowerCase();
    if (activeTab === 0) {
      setModuleData(filterData.filter(f => f.description?.toLowerCase().includes(lower) || f.id?.toString().includes(lower)));
    } else {
      setProcessorData(processorFilterData.filter(f => 
        f.code?.toLowerCase().includes(lower) || 
        f.description?.toLowerCase().includes(lower)
      ));
    }
  };

  return {
    loading,
    moduleData,
    processorData,
    showMessage,
    message,
    isSuccess,
    searchData,
    updateAccess,
    saveProcessorAccess,
    showGeneralMessage,
    refreshModuleAccessData: fetchModuleAccessData,
    refreshProcessorAccessData: fetchProcessorAccessData,
  };
};